//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { DepositTypeToString, DynamicDepositStatusEnum, yupErc20Wei, yupEthAddr } from './Enums'
import { EthAddr, StringTypeEnum, toFieldMetaData, TxHash } from './SchemaTypes'
import { EventNameForTemplate, EventTypeForTemplate } from './templateApx'

export const DynamicWithdrawalActivityCTOSchema = yup
  .object({
    stage: yup
      .number()
      .label('stage')
      .meta(toFieldMetaData({ propName: 'stage', description: 'stage' })),
    poolAddr: yupEthAddr.label('Vault Address').meta(
      toFieldMetaData({
        propName: 'poolAddr',
        description: 'Address of Vault ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    lender: yupEthAddr.label('Wallet Address').meta(
      toFieldMetaData({
        propName: 'lender',
        description: 'Wallet Address of Lender ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    shares: yupErc20Wei.label('Requested Shared').meta(
      toFieldMetaData({
        propName: 'shares',
        description: 'shares ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
    assets: yupErc20Wei.label('Requested Shared').meta(
      toFieldMetaData({
        propName: 'assets',
        description: 'assets ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    fundsDestination: yupEthAddr.label('Wallet Address').meta(
      toFieldMetaData({
        propName: 'fundsDestination',
        description: 'Wallet Address of fundsDestination ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    uuid: yup
      .string()
      .label('uuid')
      .meta(toFieldMetaData({ propName: 'uuid', description: 'uuid' })),
  })
  .label('WithdrawalActivityCTO')

export interface DynamicWithdrawalActivityCTO extends InferType<typeof DynamicWithdrawalActivityCTOSchema> {
  poolAddr: EthAddr
  lender: EthAddr
  shares: ERC20Wei
  assets: ERC20Wei
  uuid: string
}

export const DynamicDepositActivityCTOSchema = yup
  .object({
    poolAddr: yupEthAddr.label('Vault Address').meta(
      toFieldMetaData({
        propName: 'poolAddr',
        description: 'Address of Vault ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    lender: yupEthAddr.label('Wallet Address').meta(
      toFieldMetaData({
        propName: 'lender',
        description: 'Wallet Address of Lender ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    shares: yupErc20Wei.label('Requested Shared').meta(
      toFieldMetaData({
        propName: 'shares',
        description: 'shares ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
    assets: yupErc20Wei.label('Requested Shared').meta(
      toFieldMetaData({
        propName: 'assets',
        description: 'assets ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    fundsOrigination: yupEthAddr.label('Wallet Address').meta(
      toFieldMetaData({
        propName: 'fundsDestination',
        description: 'Wallet Address of fundsDestination ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    txHash: yup
      .string()
      .label('txHash')
      .meta(toFieldMetaData({ propName: 'txHash', description: 'uuid' })),

    state: yup
      .string()
      .label('State')
      .meta(
        toFieldMetaData({
          propName: 'state',
          description: 'The State of the Deposit',
        })
      ),
  })
  .label('WithdrawalActivityCTO')

export interface DynamicDepositActivityCTO extends InferType<typeof DynamicDepositActivityCTOSchema> {
  poolAddr: EthAddr
  lender: EthAddr
  shares?: ERC20Wei
  assets: ERC20Wei
  txHash: TxHash
  state: DynamicDepositStatusEnum
}

export const WithdrawalActivityCTOSchema = yup
  .object({
    requestedShares: yupErc20Wei.label('Requested Shared').meta(
      toFieldMetaData({
        propName: 'requestedShares',
        description: 'Requested Shares ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
    requestedAssets: yupErc20Wei
      .optional()
      .label('Requested Shared')
      .meta(
        toFieldMetaData({
          propName: 'requestedAssets',
          description: 'Requested Assets ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    requestedPrincipal: yupErc20Wei
      .optional()
      .label('Requested Principal')
      .meta(
        toFieldMetaData({
          propName: 'requestedPrincipal',
          description: 'Requested Assets ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    requestedRepayment: yupErc20Wei
      .optional()
      .label('Requested Repayment')
      .meta(
        toFieldMetaData({
          propName: 'requestedRepayment',
          description: 'Requested Repayment ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    requestTimestamp: yup
      .number()
      .label('Request Timestamp')
      .meta(toFieldMetaData({ propName: 'requestTimestamp', description: 'Time of withdraw request' })),
    processedTimestamp: yup
      .number()
      .label('Processed Timestamp')
      .meta(toFieldMetaData({ propName: 'processedTimestamp', description: 'Timestamp withdraw processing' })),
    repaidTimestamp: yup
      .number()
      .label('Repaid Timestamp')
      .meta(toFieldMetaData({ propName: 'repaidTimestamp', description: 'Timestamp of withdraw repayment' })),
    lender: yupEthAddr.label('Wallet Address').meta(
      toFieldMetaData({
        propName: 'lender',
        description: 'Wallet Address of Lender ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    loanAddr: yupEthAddr
      .optional()
      .label('Loan Address')
      .meta(
        toFieldMetaData({
          propName: 'loanAddr',
          description: 'The Loan address . ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    status: yup
      .string()
      .label('Withdraw Status')
      .meta(toFieldMetaData({ propName: 'status', description: 'Status of withdraw' })),

    eventId: yupErc20Wei
      .optional()
      .label('EventId')
      .meta(
        toFieldMetaData({
          propName: 'eventId',
          description: 'Withdraw event id',
          stringType: StringTypeEnum.UINT32,
        })
      ),

    transactionHash: yup
      .string()
      .required()
      .label('Transaction Hash')
      .meta(toFieldMetaData({ propName: 'transactionHash', stringType: StringTypeEnum.TX_HASH })),
  })
  .label('WithdrawalActivityCTO')

export interface WithdrawalActivityCTO extends InferType<typeof WithdrawalActivityCTOSchema> {}

export const PoolEventSchema = yup
  .object({
    id: yup
      .string()
      .label('PoolEventId ')
      .required()
      .meta(toFieldMetaData({ propName: 'id', description: 'ID of event ', stringType: StringTypeEnum.ID })),
    poolAddr: yupEthAddr.label('Vault Address').meta(
      toFieldMetaData({
        propName: 'poolAddr',
        description: 'The smart-contract address of the loan vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    poolName: yup
      .string()
      .label('Vault Name')
      .meta(toFieldMetaData({ propName: 'poolName', description: 'Name of the Loan Vault' })),

    poolAddrAccountAddr: yup
      .string()
      .label('pool lender address key')
      .meta(
        toFieldMetaData({
          propName: 'poolAddrAccountAddr',
          description: 'pool lender address key ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    eventType: yup
      .string()
      .required()
      .label('Event Label')
      .meta(toFieldMetaData({ propName: 'eventType', description: 'Name of Event' })),
    // status: yup
    //   .string()
    //   .label('Withdraw Status')
    //   .meta(toFieldMetaData({ propName: 'status', description: 'Status of withdraw' })),

    eventName: yup
      .string()
      .required()
      .label('Event Name')
      .meta(toFieldMetaData({ propName: 'eventName', description: 'Name of Event' })),

    currency: yup
      .string()
      .label('Currency Symbol')
      .meta(toFieldMetaData({ propName: 'currency', description: 'currency' })),
    symbol: yup
      .string()
      .label('Token Symbol')
      .meta(toFieldMetaData({ propName: 'symbol', description: 'Symbol for Vault Tokens', example: 'OTT' })),

    accountAddr: yupEthAddr
      .label('Lender Address')

      .meta(
        toFieldMetaData({
          propName: 'accountAddr',
          description: 'The Lender address . ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    withdrawSequenceId: yup
      .string()
      .label('withdrawSequenceId')
      .meta(toFieldMetaData({ propName: 'withdrawSequenceId', description: 'withdrawSequenceId' })),
    withdrawStage: yup
      .string()
      .label('withdrawStage')
      .meta(toFieldMetaData({ propName: 'withdrawStage', description: 'withdrawStage' })),
    loanAddr: yupEthAddr.label('Loan Address').meta(
      toFieldMetaData({
        propName: 'loanAddr',
        description: 'The Loan address . ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    receiverAddr: yupEthAddr.label('Receiver Address').meta(
      toFieldMetaData({
        propName: 'receiverAddr',
        description: 'The Receiver address . ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    senderAddr: yupEthAddr.label('Sender Address').meta(
      toFieldMetaData({
        propName: 'senderAddr',
        description: 'The Sender address . ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    priorLoanAddr: yupEthAddr.label('Prior Loan Address').meta(
      toFieldMetaData({
        propName: 'priorLoanAddr',
        description: 'The Prior Loan address . ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    blockNumber: yup
      .number()
      .label('Block Number')
      .required()
      .meta(toFieldMetaData({ propName: 'blockNumber', description: 'Block number of the data' })),

    transactionHash: yup
      .string()
      .required()
      .label('Transaction Hash')
      .meta(toFieldMetaData({ propName: 'transactionHash', stringType: StringTypeEnum.TX_HASH })),
    timestamp: yup
      .number()
      .label('Timestamp')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'timestamp',
          description: 'Timestamp of the data',
          stringType: StringTypeEnum.ETH_DATE_TIME,
        })
      ),
    transferOutDayTimestamp: yup
      .number()
      .label('transferOutDayTimestamp')
      .meta(toFieldMetaData({ propName: 'transferOutDayTimestamp', description: 'transferOutDayTimestamp' })),
    transferInDayTimestamp: yup
      .number()
      .label('transferInDayTimestamp')
      .meta(toFieldMetaData({ propName: 'transferInDayTimestamp', description: 'transferInDayTimestamp' })),
    chainId: yup
      .number()
      .required()
      .label('Chain Id')
      .meta(toFieldMetaData({ propName: 'chainId', example: '5', description: 'ChainId of the Data' })),

    assets: yupErc20Wei
      .label('Assets')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'assets',
          description: 'Assets',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    liquidityTokenSymbol: yup
      .string()
      .label('Liquidity Token Symbol')
      .meta(toFieldMetaData({ propName: 'liquidityTokenSymbol', description: 'liquidityTokenSymbol' })),

    shares: yupErc20Wei
      .label('Vault Tokens')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'shares',
          description: 'shares',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),

    assetsFromPool: yupErc20Wei.label('assetsFromPool').meta(
      toFieldMetaData({
        propName: 'assetsFromPool',
        description: 'assetsFromPool',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    assetsFromPriorToNextLoan: yupErc20Wei.label('assetsFromPriorToNextLoan').meta(
      toFieldMetaData({
        propName: 'assetsFromPriorToNextLoan',
        description: 'assetsFromPriorToNextLoan',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    assetToReturnToPool: yupErc20Wei.label('assetToReturnToPool').meta(
      toFieldMetaData({
        propName: 'assetToReturnToPool',
        description: 'assetToReturnToPool',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    fees: yupErc20Wei.label('fees').meta(
      toFieldMetaData({
        propName: 'fees',
        description: 'fees',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    principal: yupErc20Wei.label('principal').meta(
      toFieldMetaData({
        propName: 'principal',
        description: 'principal',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    repayment: yupErc20Wei.label('repayment').meta(
      toFieldMetaData({
        propName: 'repayment',
        description: 'repayment',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    liquidityAssetAddr: yupEthAddr.label('Liquidity Asset Address').meta(
      toFieldMetaData({
        propName: 'liquidityAssetAddr',
        description: 'The smart-contract address for the liquidity asset e.g. USDC, EURC. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    poolLiquidityAssetBalance: yupErc20Wei.label('poolLiquidityAssetBalance').meta(
      toFieldMetaData({
        propName: 'poolLiquidityAssetBalance',
        description: 'poolLiquidityAssetBalance',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

    borrowerVaultLiquidityAssetBalance: yupErc20Wei.label('borrowerVaultLiquidityAssetBalance').meta(
      toFieldMetaData({
        propName: 'borrowerVaultLiquidityAssetBalance',
        description: 'borrowerVaultLiquidityAssetBalance',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

    borrowerWalletLiquidityAssetBalance: yupErc20Wei.label('borrowerWalletLiquidityAssetBalance').meta(
      toFieldMetaData({
        propName: 'borrowerWalletLiquidityAssetBalance',
        description: 'borrowerWalletLiquidityAssetBalance',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

    feeVaultLiquidityAssetBalance: yupErc20Wei.label('feeVaultLiquidityAssetBalance').meta(
      toFieldMetaData({
        propName: 'feeVaultLiquidityAssetBalance',
        description: 'feeVaultLiquidityAssetBalance',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    poolAdminLiquidityAssetBalance: yupErc20Wei.label('poolAdminLiquidityAssetBalance').meta(
      toFieldMetaData({
        propName: 'poolAdminLiquidityAssetBalance',
        description: 'poolAdminLiquidityAssetBalance',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    accountLiquidityAssetBalance: yupErc20Wei.label('accountLiquidityAssetBalance').meta(
      toFieldMetaData({
        propName: 'accountLiquidityAssetBalance',
        description: 'accountLiquidityAssetBalance',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    accountTokenBalance: yupErc20Wei.label('accountTokenBalance').meta(
      toFieldMetaData({
        propName: 'accountTokenBalance',
        description: 'accountTokenBalance',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
    interestAccrued: yupErc20Wei.label('Interest Accrued').meta(
      toFieldMetaData({
        propName: 'interestAccrued',
        description: 'Interest Accrued for the loan at Rollover',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    emailStatus: yup
      .string()
      .optional()
      .label('Email Status')
      .meta(
        toFieldMetaData({
          propName: 'emailStatus',
          description: 'Status of email sent',
        })
      ),
    depositType: yup
      .number()
      .optional()
      .label('Deposit Type')
      .meta(
        toFieldMetaData({
          propName: 'depositType',
          description: 'The Deposit Type',
          stateToString: DepositTypeToString,
        })
      ),

    transferTxHash: yup
      .string()
      .optional()
      .label('Transfer Transaction Hash')
      .meta(toFieldMetaData({ propName: 'transferTxHash', stringType: StringTypeEnum.TX_HASH })),
  })
  .label('PoolEventCTO')
  .meta(toFieldMetaData({ propName: 'PoolEventCTO', description: 'Vault Event' }))

export enum PoolEventEmailStatus {
  Initial = 'Initial',
  NoEmail = 'NoEmail',
  Queued = 'Queued',
  Sending = 'Sending',
  Failed = 'Failed',
  Sent = 'Sent',
  Regenerated = 'Regenerated',
}
export interface PoolEventCTO extends InferType<typeof PoolEventSchema> {
  poolAddr: EthAddr
  accountAddr: EthAddr
  liquidityAssetAddr: EthAddr
  eventName: EventNameForTemplate
  receiverAddr?: EthAddr

  eventType: EventTypeForTemplate

  shares: ERC20Wei
  assets: ERC20Wei
  principal: ERC20Wei
  fees: ERC20Wei
  emailStatus: PoolEventEmailStatus | undefined
  depositType?: number
  transferTxHash?: TxHash
  liquidityTokenSymbol: string
  withdrawSequenceId?: string
}
